import { createEffect, createEvent, createStore, sample } from 'effector';
import { axiosClient } from '@nx-frontend/shared/api';
import { generalEnv } from '@nx-frontend/shared/env';
import { Org, OrgLimits, OrgServiceOrgLimitsDto } from '@nx-frontend/shared/types';

export interface OrgLimitsStore {
  isLoaded: boolean;
  isFetching: boolean;
  data: OrgLimits;
}

export const initialOrgLimitsState: OrgLimitsStore = {
  isLoaded: false,
  isFetching: false,
  data: {
    membersPerOrg: 0,
    ocr: 0,
    maxMembers: 0,
    baseWorkspaces: 0,
    workspaces: 0,
    encryptionKeysPerWorkspace: 0,
    membersPerWorkspace: 0,
    traffic: 0,
    baseTraffic: 0,
    storage: 0,
    baseStorage: 0,
    textSize: 0,
    attachmentSize: 0,
    notesPerWorkspace: 0,
    streamingVideosPerMonth: 0,
    blots: 0,
    guestsPerMember: 0,
    taskDeadlinesPerWorkspace: 0,
    taskRemindersPerWorkspace: 0,
    guests: 0,
  },
};

export const $orgLimits = createStore<OrgLimitsStore>(initialOrgLimitsState);
export const $orgLimitsData = $orgLimits.map((state) => state.data);

export const loadOrgLimitsEvent = createEvent<Org>();
const setOrgLimitsFetchingEvent = createEvent();

const loadOrgLimitsFx = createEffect(async ({ orgId }: { orgId: string }) => {
  const { data } = await axiosClient.get<OrgServiceOrgLimitsDto>(`${generalEnv.nextApiPath}/orgs/${orgId}/limits`);
  return data;
});

$orgLimits.on(setOrgLimitsFetchingEvent, (store) => {
  if (store.isFetching || store.isLoaded) {
    return store;
  }
  return Object.assign({}, store, {
    isFetching: true,
  });
});

$orgLimits.on(loadOrgLimitsFx.doneData, (orgUsageStore, data) => {
  return Object.assign({}, orgUsageStore, {
    data,
    isLoaded: true,
    isFetching: false,
  });
});

$orgLimits.on(loadOrgLimitsFx.failData, (orgUsageStore, data) => {
  console.error('Fail to fetch org limits', data);
  return Object.assign({}, orgUsageStore, {
    isFetching: false,
  });
});

export const $isOrgLimitsLoaded = $orgLimits.map((orgLimitsStore) => {
  if (!orgLimitsStore) {
    return false;
  }
  return orgLimitsStore.isLoaded;
});

sample({
  source: $orgLimits,
  clock: loadOrgLimitsEvent,
  filter(orgLimitsStore, currentOrganization) {
    if (orgLimitsStore.isFetching || orgLimitsStore.isLoaded || !currentOrganization || !currentOrganization.globalId) {
      // do not load if already fetching or loaded
      return false;
    }
    return true;
  },
  fn: (orgUsageStore, currentOrganization) => {
    return {
      orgId: currentOrganization.globalId,
    };
  },
  target: [loadOrgLimitsFx, setOrgLimitsFetchingEvent],
});
